import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { DataGrid } from "@mui/x-data-grid";
import { apiGet } from "../../shared/apiClient";
import { deleteVendor, fetchVendorAddressesList, fetchVendorsList } from "../../shared/vendorMastersApi";
import { IconEye, IconPlus } from "../../shared/toolbarIcons";
import { RecordViewDrawer } from "../../shared/RecordDrawer";
import { DEFAULT_PAGE_SIZE_OPTIONS } from "../../shared/useClientPagination";

function pickDisplayAddress(addresses, vendorId) {
  const list = addresses.filter((a) => a.vendorId === vendorId);
  if (list.length === 0) return null;
  const def = list.find((a) => a.isDefault);
  return def ?? list[0];
}

function formatLocation(addr, countries, states, cities) {
  if (!addr) return "-";
  const country = countries.find((c) => c.id === addr.countryId);
  const state = states.find((s) => s.id === addr.stateId);
  const city = cities.find((ci) => ci.id === addr.cityId);
  return [city?.name, state?.name, country?.name].filter(Boolean).join(", ") || "-";
}

function formatDateTime(value) {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" });
}

function truncateText(text, max = 36) {
  if (text == null || text === "") return "-";
  const s = String(text);
  if (s.length <= max) return s;
  return `${s.slice(0, max)}…`;
}

function vendorTaxNo(v) {
  return v.taxNo ?? v.tAXNo ?? "-";
}

function formatRating(v) {
  if (v.avgRating != null || v.ratingCount != null) {
    return `${Number(v.avgRating ?? 0).toFixed(1)} (${v.ratingCount ?? 0})`;
  }
  return "-";
}

export function VendorsList() {
  const navigate = useNavigate();
  const [vendors, setVendors] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [viewRow, setViewRow] = useState(null);

  const rows = useMemo(() => {
    return vendors.map((v) => {
      const addr = pickDisplayAddress(addresses, v.id);
      const addressLine = addr?.address ?? "-";
      const location = formatLocation(addr, countries, states, cities);
      return {
        id: v.id,
        vendor: v,
        addressLine,
        location
      };
    });
  }, [vendors, addresses, countries, states, cities]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const [vRes, aRes, countryRes, stateRes, cityRes] = await Promise.all([
      fetchVendorsList(),
      fetchVendorAddressesList(),
      apiGet("/api/Countries"),
      apiGet("/api/States"),
      apiGet("/api/Cities")
    ]);
    if (!vRes.ok) {
      setError(vRes.error ?? "Failed to load vendors");
      setVendors([]);
    } else {
      setVendors(vRes.items);
    }
    if (!aRes.ok) {
      setError((prev) => prev ?? aRes.error ?? "Failed to load vendor addresses");
      setAddresses([]);
    } else {
      setAddresses(aRes.items);
    }
    if (countryRes.ok) setCountries(countryRes.data ?? []);
    if (stateRes.ok) setStates(stateRes.data ?? []);
    if (cityRes.ok) setCities(cityRes.data ?? []);
    setLoading(false);
  }, []);

  const handleDeleteClick = useCallback(
    async (id) => {
      if (!(await window.confirmDialog("Delete this vendor?"))) return;
      const res = await deleteVendor(id);
      if (!res.ok) setError(res.error ?? "Failed to delete vendor");
      else await load();
    },
    [load]
  );

  const columns = useMemo(
    () => [
      {
        field: "name",
        headerName: "Name",
        flex: 0.85,
        minWidth: 110,
        valueGetter: (_v, row) => row.vendor.name ?? "-"
      },
      {
        field: "slug",
        headerName: "Slug",
        minWidth: 100,
        valueGetter: (_v, row) => row.vendor.slug ?? "-"
      },
      {
        field: "email",
        headerName: "Email",
        flex: 1,
        minWidth: 140,
        valueGetter: (_v, row) => row.vendor.email ?? "-"
      },
      {
        field: "phoneNo",
        headerName: "Phone",
        minWidth: 110,
        valueGetter: (_v, row) => row.vendor.phoneNo ?? "-"
      },
      {
        field: "addressLine",
        headerName: "Address",
        flex: 0.9,
        minWidth: 110,
        renderCell: (params) => {
          const raw = params.row.addressLine ?? "-";
          const short = truncateText(raw, 28);
          return (
            <span title={raw !== "-" ? raw : undefined} style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
              {short}
            </span>
          );
        }
      },
      {
        field: "location",
        headerName: "Location",
        flex: 0.85,
        minWidth: 100,
        renderCell: (params) => {
          const raw = params.row.location ?? "-";
          const short = truncateText(raw, 24);
          return (
            <span title={raw !== "-" ? raw : undefined} style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
              {short}
            </span>
          );
        }
      },
      {
        field: "commission",
        headerName: "Commission",
        minWidth: 100,
        valueGetter: (_v, row) => {
          const c = row.vendor.commission;
          return c != null ? String(c) : "-";
        }
      },
      {
        field: "taxNo",
        headerName: "TAX No",
        minWidth: 90,
        valueGetter: (_v, row) => vendorTaxNo(row.vendor)
      },
      {
        field: "licenseNo",
        headerName: "License",
        minWidth: 90,
        valueGetter: (_v, row) => row.vendor.licenseNo ?? "-"
      },
      {
        field: "panNo",
        headerName: "PAN",
        minWidth: 90,
        valueGetter: (_v, row) => row.vendor.panNo ?? "-"
      },
      {
        field: "description",
        headerName: "Description",
        flex: 0.8,
        minWidth: 100,
        renderCell: (params) => {
          const v = params.row.vendor;
          const raw = v.description ?? "-";
          const short = truncateText(raw, 32);
          return (
            <span title={raw !== "-" ? String(v.description ?? "") : undefined} style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
              {short}
            </span>
          );
        }
      },
      {
        field: "actions",
        headerName: "Actions",
        width: 170,
        align: "center",
        headerAlign: "center",
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => {
          const v = params.row.vendor;
          return (
            <div className="table-actions">
              <button className="icon-button" type="button" title="View" onClick={() => setViewRow(params.row)}>
                <IconEye />
              </button>
              <button className="icon-button" type="button" title="Edit" onClick={() => navigate(`/vendors?edit=${v.id}`)}>
                ✏️
              </button>
              <button
                className="icon-button danger"
                type="button"
                title="Delete"
                onClick={() => void handleDeleteClick(v.id)}
              >
                🗑️
              </button>
            </div>
          );
        }
      }
    ],
    [navigate, handleDeleteClick]
  );

  useEffect(() => {
    void load();
  }, [load]);

  const total = rows.length;

  return (
    <div className="card">
      <div className="card-header card-header--row">
        <div>
          <h2>Vendors</h2>
          <p>{loading ? "Loading vendors..." : `Total: ${total}`}</p>
        </div>
        <div className="card-header-actions">
          <Link to="/vendors" className="icon-button icon-button--toolbar" title="Add vendor" aria-label="Add vendor">
            <IconPlus />
          </Link>
        </div>
      </div>
      <div className="card-body">
        {error && <div className="error-banner">{error}</div>}
        <div className="masters-data-grid-shell">
          <DataGrid
            rows={rows}
            columns={columns}
            loading={loading}
            disableRowSelectionOnClick
            pagination
            paginationMode="client"
            pageSizeOptions={DEFAULT_PAGE_SIZE_OPTIONS}
            initialState={{
              pagination: { paginationModel: { page: 0, pageSize: DEFAULT_PAGE_SIZE_OPTIONS[0] } }
            }}
            sx={{
              border: "none",
              fontFamily: "inherit",
              fontSize: "0.9rem",
              height: "100%",
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "#f2fcf5",
                borderBottom: "1px solid #e8ecea",
                fontWeight: 600
              },
              "& .MuiDataGrid-footerContainer": {
                borderTop: "1px solid #e8ecea",
                backgroundColor: "#fbfffc"
              },
              "& .MuiDataGrid-row:nth-of-type(even):not(.MuiDataGrid-row--editing):not(:hover)": {
                backgroundColor: "#fcfffd"
              },
              "& .MuiDataGrid-cell": {
                borderBottomColor: "#e8ecea"
              }
            }}
          />
        </div>
      </div>
      <RecordViewDrawer
        open={Boolean(viewRow)}
        onClose={() => setViewRow(null)}
        label="Vendor details"
        title={viewRow?.vendor?.name ?? "—"}
        subtitle={viewRow?.vendor?.slug}
        ariaLabel="Vendor details"
        images={
          viewRow
            ? [
                viewRow.vendor.photo ? { label: "Photo", src: viewRow.vendor.photo, linkText: "Open photo" } : null,
                viewRow.vendor.banner ? { label: "Banner", src: viewRow.vendor.banner, linkText: "Open banner" } : null
              ].filter(Boolean)
            : []
        }
        sections={
          viewRow
            ? [
                {
                  title: "Vendor Details",
                  columns: 2,
                  fields: [
                    ["Name", viewRow.vendor.name ?? "-"],
                    ["Slug", viewRow.vendor.slug ?? "-"],
                    ["Email", viewRow.vendor.email ?? "-"],
                    ["Phone", viewRow.vendor.phoneNo ?? "-"],
                    ["Commission", viewRow.vendor.commission ?? "-"],
                    ["TAX No", vendorTaxNo(viewRow.vendor)],
                    ["License No", viewRow.vendor.licenseNo ?? "-"],
                    ["PAN No", viewRow.vendor.panNo ?? "-"],
                    ["Status", viewRow.vendor.status ?? "Active"],
                    ["Rating", formatRating(viewRow.vendor)]
                  ]
                },
                {
                  title: "Address Details",
                  fields: [
                    ["Address", viewRow.addressLine ?? "-", true],
                    ["Location", viewRow.location ?? "-"]
                  ]
                },
                {
                  title: "Content",
                  fields: [
                    ["Description", viewRow.vendor.description ?? "-", true],
                    ["Catalog", viewRow.vendor.catelog ?? viewRow.vendor.catalog ?? "-"],
                    ["License File", viewRow.vendor.licenseFile ?? "-"]
                  ]
                },
                {
                  title: "Audit Details",
                  columns: 2,
                  fields: [
                    ["Created", formatDateTime(viewRow.vendor.createdDate)],
                    ["Updated", formatDateTime(viewRow.vendor.updatedDate)]
                  ]
                }
              ]
            : []
        }
      />
    </div>
  );
}
